import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import env from '@/networkRequests/apiClient/env.config'
import routes from '@/router/routes.dictionary'
import localStorage from '@/shared/functions/LocalStorage/localStorage'
import { Box } from '@mui/material'

import PaginatedPage from '../../shared/components/PaginatedPage/PaginatedPage.component'
import UploadFlightItineraryPDFModal from '../../shared/components/UploadFlightItineraryPDFModal/UploadFlightItineraryPDFModal.component'
import OfferItem from './components/OfferItem/OfferItem.component'

import { FlightItinerary as UploadFlightItinerary } from '../../redux/states/flights/externalFlightBookings/uploadPdf/uploadPdf.types'
import { getAllFlightItineraries } from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.slice'
import {
  FlightItinerary,
  GetAllFlightItinerariesRequest
} from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { getPublicOffers } from '../../redux/states/offerRequest/getPublicOffers/getPublicOffers.slice'
import {
  FulfillmentOrder,
  GetPublicOffersRequest,
  OfferRequest
} from '../../redux/states/offerRequest/getPublicOffers/getPublicOffers.types'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import { EmptyStateProps } from '../../shared/components/EmptyState/EmptyState.types'
import Modal from '../../shared/components/Modal/Modal.functions'
import {
  FilterTab,
  PaginatedRenderProps,
  PaginatedResponse,
  SearchBarProps
} from '../../shared/components/PaginatedPage/PaginatedPage.types'
import {
  FilterSectionConfig,
  Filters
} from '../../shared/components/UniversalFilterSideBar/UniversalFilterSideBar.types'
import { GRAMS_PER_POUND } from '../../shared/dictionaries/unitConversion.dictionary'
import { isAuthenticated } from '../../shared/functions/Auth/auth.functions'
import { UserRole } from '../../shared/functions/UserRole/userRoleManagement.types'
import { getUserRole, setUserRole } from '../../shared/functions/UserRole/userRoleManagment.functions'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import { SHARED_OFFER_DETAILS_KEY } from './Offers.dictionary'
import { convertToPounds } from './Offers.functions'
import styles from './Offers.styles'
import { SharedFulfillmentOrderDetails, SharedOfferDetails } from './components/OfferItem/OfferItem.types'

const Offers: React.FC = () => {
  const { loading } = useAppSelector((state: RootState) => state.getPublicOffers)
  const [flightItineraries, setFlightItineraries] = useState<FlightItinerary[]>([])
  const [addFlightModalOpen, setAddFlightModalOpen] = useState(false)
  const [addFlightOfferContext, setAddFlightOfferContext] = useState<(OfferRequest | FulfillmentOrder) | null>(null)
  const [orderIdAcceptedFromAddFlight, setOrderIdAcceptedFromAddFlight] = useState<string | null>(null)
  const activeTabRef = useRef<'all' | 'airport-pickup' | 'amazon-orders'>(
    getUserRole() === UserRole.sender ? 'airport-pickup' : 'all'
  )

  const dispatch = useAppDispatch()

  // Get values once - either from localStorage or URL
  const urlShopperId = router.getUrlParam('shopperId')
  const urlOfferRequestIds = router.getUrlParam('offerRequestIds')
  const urlFulfillmentOrderIds = router.getUrlParam('fulfillmentOrderIds')
  const urlSenderId = router.getUrlParam('senderId')
  const initialSharedOfferDetails = localStorage.getItem(SHARED_OFFER_DETAILS_KEY) as
    | SharedOfferDetails
    | SharedFulfillmentOrderDetails
    | null

  const [shopperId] = useState<string | null>(() => {
    if (initialSharedOfferDetails && 'shopperId' in initialSharedOfferDetails) {
      return (initialSharedOfferDetails as SharedOfferDetails).shopperId
    }

    return urlShopperId
  })

  const [offerRequestIds] = useState<string[] | null>(() => {
    const urlIds = urlOfferRequestIds ? urlOfferRequestIds.split(',').filter((id) => id.trim() !== '') : []
    const localStorageDetails = initialSharedOfferDetails

    if (localStorageDetails && 'offerRequestId' in localStorageDetails) {
      const localStorageId = (localStorageDetails as SharedOfferDetails).offerRequestId

      if (localStorageId && !urlIds.includes(localStorageId)) {
        return [localStorageId, ...urlIds]
      }
    }

    return urlIds.length > 0 ? urlIds : null
  })

  const [fulfillmentOrderIds] = useState<string[] | null>(() => {
    const urlIds = urlFulfillmentOrderIds
      ? urlFulfillmentOrderIds
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      : []
    const localStorageDetails = initialSharedOfferDetails

    if (localStorageDetails && 'orderId' in localStorageDetails && 'senderId' in localStorageDetails) {
      const sharedFulfillment = localStorageDetails as SharedFulfillmentOrderDetails

      if (sharedFulfillment.orderId && !urlIds.includes(sharedFulfillment.orderId)) {
        return [sharedFulfillment.orderId, ...urlIds]
      }
    }

    return urlIds.length > 0 ? urlIds : null
  })

  const [senderId] = useState<string | null>(() => {
    if (initialSharedOfferDetails && 'senderId' in initialSharedOfferDetails) {
      return (initialSharedOfferDetails as SharedFulfillmentOrderDetails).senderId
    }

    return urlSenderId
  })

  useEffect(() => {
    const hasSharedFulfillmentOrderParams =
      urlFulfillmentOrderIds && urlFulfillmentOrderIds.trim() !== '' && urlSenderId && urlSenderId.trim() !== ''

    if (hasSharedFulfillmentOrderParams && getUserRole() !== UserRole.traveler) {
      setUserRole(UserRole.traveler)
      window.location.reload()
    }
  }, [urlFulfillmentOrderIds, urlSenderId])

  useEffect(() => {
    if (initialSharedOfferDetails) {
      const timeoutId = setTimeout(() => {
        localStorage.removeItem(SHARED_OFFER_DETAILS_KEY)
      }, 100)

      return () => clearTimeout(timeoutId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchOffers = useCallback(
    async (page: number): Promise<PaginatedResponse<OfferRequest | FulfillmentOrder>> => {
      return new Promise((resolve) => {
        const hasSharedFulfillmentOrders = fulfillmentOrderIds && fulfillmentOrderIds.length > 0 && senderId
        const effectiveType = hasSharedFulfillmentOrders
          ? 'fulfillment_order'
          : activeTabRef.current === 'airport-pickup'
            ? 'fulfillment_order'
            : activeTabRef.current === 'amazon-orders'
              ? 'offer_request'
              : undefined

        const getPublicOffersRequest: GetPublicOffersRequest = {
          page,
          size: env.DEFAULT_PAGE_SIZE,
          searchQuery: '',
          shopperId: shopperId || undefined,
          offerRequestIds: offerRequestIds || undefined,
          fulfillmentOrderIds: fulfillmentOrderIds || undefined,
          senderId: senderId || undefined,
          type: effectiveType
        }

        dispatch(
          getPublicOffers({
            request: getPublicOffersRequest,
            onSuccess: (response) => {
              resolve({
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
                results: response.data.results
              })
            }
          })
        )
      })
    },
    [dispatch, shopperId, offerRequestIds, fulfillmentOrderIds, senderId]
  )

  useEffect(() => {
    const fetchFlightItineraries = async () => {
      if (!isAuthenticated()) return

      const getAllFlightItinerariesPayload: GetAllFlightItinerariesRequest = {}

      const response = await dispatch(
        getAllFlightItineraries({
          request: getAllFlightItinerariesPayload
        })
      ).unwrap()

      setFlightItineraries(response.data.results)
    }

    fetchFlightItineraries()
  }, [dispatch])

  const handleAddMyFlightOpen = useCallback((offer: OfferRequest | FulfillmentOrder) => {
    Modal.close()
    setAddFlightOfferContext(offer)
    setAddFlightModalOpen(true)
  }, [])

  const handleAddFlightModalClose = useCallback(() => {
    setAddFlightModalOpen(false)
    setAddFlightOfferContext(null)
  }, [])

  const handleAddFlightModalSubmit = useCallback(
    (_values: UploadFlightItinerary) => {
      const acceptedOrderId =
        addFlightOfferContext && 'orderId' in addFlightOfferContext ? addFlightOfferContext.orderId : null

      setAddFlightModalOpen(false)
      setAddFlightOfferContext(null)

      if (acceptedOrderId) {
        setOrderIdAcceptedFromAddFlight(acceptedOrderId)
      }

      dispatch(getAllFlightItineraries({ request: {} }))
        .unwrap()
        .then((response) => setFlightItineraries(response.data.results))
    },
    [dispatch, addFlightOfferContext]
  )

  const OfferItemsView = function (props: PaginatedRenderProps) {
    const item = props.item as OfferRequest | FulfillmentOrder

    const sharedOfferDetails =
      'offerRequestId' in item
        ? {
            offerRequestId: item.offerRequestId.toString(),
            shopperId: shopperId || ''
          }
        : undefined

    const sharedFulfillmentOrderDetails =
      'orderId' in item && fulfillmentOrderIds && fulfillmentOrderIds.length > 0 && senderId
        ? {
            type: 'fulfillment_order' as const,
            orderId: item.orderId,
            senderId: senderId
          }
        : undefined

    return (
      <Box
        sx={styles.offerItemContainer}
        aria-label={('orderId' in item ? 'fulfillment-order' : 'offer-item') + '-container'}
      >
        <OfferItem
          offer={item}
          flightItineraries={flightItineraries}
          sharedOfferDetails={sharedOfferDetails}
          sharedFulfillmentOrderDetails={sharedFulfillmentOrderDetails}
          onAddMyFlight={handleAddMyFlightOpen}
          orderIdAcceptedFromAddFlight={orderIdAcceptedFromAddFlight}
        />
      </Box>
    )
  }

  const searchBar: SearchBarProps = {
    placeholder: 'Type any item name',
    onSearchFilter: (items, query) => {
      const lowerQuery = query.toLowerCase()

      return (items as (OfferRequest | FulfillmentOrder)[]).filter((item) => {
        // Handle FulfillmentOrder
        if ('orderId' in item) {
          const fulfillmentOrder = item as FulfillmentOrder
          // Search by order ID, destination city, or destination country

          return (
            fulfillmentOrder.orderId.toLowerCase().includes(lowerQuery) ||
            fulfillmentOrder.destinationCity.toLowerCase().includes(lowerQuery) ||
            fulfillmentOrder.destinationCountry.toLowerCase().includes(lowerQuery)
          )
        }

        // Handle OfferRequest
        const offerRequest = item as OfferRequest

        return offerRequest.offerRequestDetails.some(
          (detail) =>
            detail.itemDetails.title.toLowerCase().includes(lowerQuery) ||
            detail.itemDetails.country.name.toLowerCase().includes(lowerQuery)
        )
      })
    }
  }

  const getEmptyState = () => {
    let emptyState: EmptyStateProps | undefined
    const hasSharedOffers = offerRequestIds && offerRequestIds.length > 0
    const hasSharedFulfillmentOrders = fulfillmentOrderIds && fulfillmentOrderIds.length > 0

    if (hasSharedOffers) {
      emptyState = {
        title:
          offerRequestIds!.length === 1 ? 'This offer is no longer available' : 'These offers are no longer available',
        subtitle: 'See all available offers.',
        button: {
          text: 'View All Offers',
          onClick: () => {
            window.location.href = routes.offers.path
          }
        }
      }
    } else if (hasSharedFulfillmentOrders) {
      emptyState = {
        title:
          fulfillmentOrderIds!.length === 1
            ? 'This fulfillment order is no longer available'
            : 'These fulfillment orders are no longer available',
        subtitle: 'See all available airport pickup orders.',
        button: {
          text: 'View All Offers',
          onClick: () => {
            window.location.href = `${routes.offers.path}?type=fulfillment_order`
          }
        }
      }
    } else if (activeTabRef.current === 'airport-pickup') {
      emptyState = {
        title: 'No airport pickup orders available',
        subtitle: 'Currently, there are no airport pickup orders available. Please check back later.',
        button: {
          text: 'View All Offers',
          onClick: () => {
            activeTabRef.current = 'all'
            window.location.reload()
          }
        }
      }
    } else {
      emptyState = {
        title: 'No offers available',
        subtitle: 'Currently, there are no offers available. Please check back later.'
      }
    }

    return emptyState
  }

  const horizontalFilterTabs: FilterTab<unknown>[] = useMemo(() => {
    if ((offerRequestIds && offerRequestIds.length > 0) || (fulfillmentOrderIds && fulfillmentOrderIds.length > 0)) {
      return []
    }

    return [
      {
        id: 'all',
        label: 'All',
        isApiFilter: true,
        filterDef: (items: unknown[]) => {
          activeTabRef.current = 'all'

          return items
        }
      },
      {
        id: 'airport-pickup',
        label: 'Airport Pickup',
        isApiFilter: true,
        filterDef: (items: unknown[]) => {
          activeTabRef.current = 'airport-pickup'

          return items.filter((item) => 'orderId' in (item as OfferRequest | FulfillmentOrder))
        }
      },
      {
        id: 'amazon-orders',
        label: 'Amazon Orders',
        isApiFilter: true,
        filterDef: (items: unknown[]) => {
          activeTabRef.current = 'amazon-orders'

          return items.filter((item) => !('orderId' in (item as OfferRequest | FulfillmentOrder)))
        }
      }
    ]
  }, [offerRequestIds, fulfillmentOrderIds])

  const filterSections: (items: unknown[], filters: Filters) => FilterSectionConfig[] = useCallback(
    (items: unknown[]) => {
      const allItems = items as (OfferRequest | FulfillmentOrder)[]
      const offers = allItems.filter((item) => !('orderId' in item)) as OfferRequest[]
      const fulfillmentOrders = allItems.filter((item) => 'orderId' in item) as FulfillmentOrder[]

      // Extract unique source and delivery country pairs from OfferRequests
      const offerDestinations = offers.flatMap((offer) =>
        offer.offerRequestDetails.map((detail) => {
          const sourceCountry = detail.itemDetails.country.name
          const deliveryCountry = offer.shopperDetails.deliveryTo.country

          return `${sourceCountry} → ${deliveryCountry}`
        })
      )

      // Extract destinations from FulfillmentOrders (format: "Source → Destination")
      // For fulfillment orders, we don't have source country, so we'll use a generic format
      const fulfillmentDestinations = fulfillmentOrders.map(
        (order) => `→ ${order.destinationCountry} (${order.destinationCity})`
      )

      const uniqueDestinations = Array.from(new Set([...offerDestinations, ...fulfillmentDestinations]))

      // Calculate weights for OfferRequests
      const offerWeights = offers.map((offer) =>
        offer.offerRequestDetails.reduce(
          (total, item) => total + convertToPounds(item.itemDetails.weight, item.itemDetails.weightUnit),
          0
        )
      )

      // Calculate weights for FulfillmentOrders (convert grams to pounds)
      const fulfillmentWeights = fulfillmentOrders.map((order) => order.totalWeightGrams / GRAMS_PER_POUND)

      const allWeights = [...offerWeights, ...fulfillmentWeights]
      let minWeight = allWeights.length > 0 ? Math.ceil(Math.min(...allWeights)) : 1
      let maxWeight = allWeights.length > 0 ? Math.ceil(Math.max(...allWeights)) : 1

      if (minWeight === 0 || !Number.isFinite(minWeight)) {
        minWeight = 1
      }
      if (!Number.isFinite(maxWeight)) {
        maxWeight = 1
      }

      // Traveler benefits only exist for OfferRequests
      const offerTravelerBenefits = offers.length > 0 ? offers.map((offer) => offer.travelerBenefit) : [0]
      const minTravelerBenefit = offerTravelerBenefits.length > 0 ? Math.floor(Math.min(...offerTravelerBenefits)) : 0
      const maxTravelerBenefit = offerTravelerBenefits.length > 0 ? Math.ceil(Math.max(...offerTravelerBenefits)) : 0

      const sections: FilterSectionConfig[] = [
        {
          id: 'destination',
          title: 'Destination',
          type: 'multi-select',
          options: [
            { primaryValue: 'All' },
            ...uniqueDestinations.map((destination) => ({ primaryValue: destination }))
          ],
          defaultValue: ['All'],
          filterDef: (item: unknown, filters) => {
            const typedItem = item as OfferRequest | FulfillmentOrder
            if (filters.destination && Array.isArray(filters.destination) && !filters.destination.includes('All')) {
              // Handle FulfillmentOrder
              if ('orderId' in typedItem) {
                const fulfillmentOrder = typedItem as FulfillmentOrder
                const itemDestination = `→ ${fulfillmentOrder.destinationCountry} (${fulfillmentOrder.destinationCity})`

                return filters.destination.includes(itemDestination)
              }

              // Handle OfferRequest
              const offerRequest = typedItem as OfferRequest
              const sourceCountry = offerRequest.offerRequestDetails[0].itemDetails.country.name
              const deliveryCountry = offerRequest.shopperDetails.deliveryTo.country
              const itemDestination = `${sourceCountry} → ${deliveryCountry}`

              return filters.destination.includes(itemDestination)
            }

            return true
          }
        },
        {
          id: 'travelerBenefitRange',
          title: 'Flight Discount',
          type: 'slider',
          options: [{ primaryValue: minTravelerBenefit }, { primaryValue: maxTravelerBenefit }],
          unit: { symbol: '$', label: 'USD' },
          defaultValue: [minTravelerBenefit, maxTravelerBenefit],
          filterDef: (item: unknown, filters) => {
            const typedItem = item as OfferRequest | FulfillmentOrder
            // FulfillmentOrders don't have travelerBenefit, so exclude them from this filter
            if ('orderId' in typedItem) {
              return true // Include all fulfillment orders (they don't have traveler benefit)
            }

            const [minTravelerBenefit, maxTravelerBenefit] = filters.travelerBenefitRange as number[]
            const offerTravelerBenefit = (typedItem as OfferRequest).travelerBenefit

            return offerTravelerBenefit >= minTravelerBenefit && offerTravelerBenefit <= maxTravelerBenefit
          }
        },
        {
          id: 'timePeriod',
          title: 'Time Period',
          type: 'multi-select',
          options: [
            { primaryValue: 'All' },
            { primaryValue: '1 Week' },
            { primaryValue: '2 Weeks' },
            { primaryValue: '3 Weeks' },
            { primaryValue: 'Anytime' }
          ],
          defaultValue: ['All'],
          filterDef: (item: unknown, filters) => {
            const typedItem = item as OfferRequest | FulfillmentOrder
            if (filters.timePeriod && Array.isArray(filters.timePeriod) && !filters.timePeriod.includes('All')) {
              // Handle FulfillmentOrder
              if ('orderId' in typedItem) {
                const fulfillmentOrder = typedItem as FulfillmentOrder
                const neededByDate = fulfillmentOrder.neededByDate

                if (filters.timePeriod.includes('Anytime') && !neededByDate) {
                  return true
                }

                if (!neededByDate) {
                  return false
                }

                const neededByDateObj = new Date(neededByDate)
                const currentDate = new Date()
                const weeksDifference = Math.ceil(
                  (neededByDateObj.getTime() - currentDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
                )
                const selectedWeeks = filters.timePeriod.map((period: string) => parseInt(period.split(' ')[0]))

                return selectedWeeks.includes(weeksDifference)
              }

              // Handle OfferRequest
              const offerRequest = typedItem as OfferRequest
              const offerNeededByDate = offerRequest.neededByDate

              if (filters.timePeriod.includes('Anytime') && !offerNeededByDate) {
                return true
              }

              if (!offerNeededByDate) {
                return false
              }

              const neededByDate = new Date(offerNeededByDate)
              const currentDate = new Date()
              const weeksDifference = Math.ceil(
                (neededByDate.getTime() - currentDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
              )
              const selectedWeeks = filters.timePeriod.map((period: string) => parseInt(period.split(' ')[0]))

              return selectedWeeks.includes(weeksDifference)
            }

            return true
          }
        }
      ]

      if (minWeight !== maxWeight) {
        sections.splice(1, 0, {
          id: 'weight',
          title: 'Weight',
          type: 'slider',
          options: [{ primaryValue: minWeight }, { primaryValue: maxWeight }],
          unit: { symbol: '', label: 'lbs' },
          defaultValue: [minWeight, maxWeight],
          filterDef: (item: unknown, filters) => {
            const typedItem = item as OfferRequest | FulfillmentOrder
            const [minWeight, maxWeight] = filters.weight as number[]

            // Handle FulfillmentOrder
            if ('orderId' in typedItem) {
              const fulfillmentOrder = typedItem as FulfillmentOrder
              const orderWeight = fulfillmentOrder.totalWeightGrams / GRAMS_PER_POUND

              return orderWeight >= minWeight - 1 && orderWeight <= maxWeight
            }

            // Handle OfferRequest
            const offerRequest = typedItem as OfferRequest
            const offerWeight = offerRequest.offerRequestDetails.reduce((total, item) => {
              if (!item.itemDetails.weightUnit) {
                return total + 1
              }

              return total + convertToPounds(item.itemDetails.weight, item.itemDetails.weightUnit || 'lbs')
            }, 0)

            return offerWeight >= minWeight - 1 && offerWeight <= maxWeight
          }
        })
      }

      return sections
    },
    []
  )

  const headerButton = useMemo(() => {
    if (offerRequestIds && offerRequestIds.length > 0) {
      return {
        text: 'View All Offers',
        buttonType: 'tertiary' as const,
        onClick: () => {
          window.location.href = routes.offers.path
        }
      }
    }

    if (fulfillmentOrderIds && fulfillmentOrderIds.length > 0) {
      return {
        text: 'View All Offers',
        buttonType: 'tertiary' as const,
        onClick: () => {
          window.location.href = `${routes.offers.path}?type=fulfillment_order`
        }
      }
    }

    return undefined
  }, [offerRequestIds, fulfillmentOrderIds])

  const pageTitle = useMemo(() => {
    if (offerRequestIds && offerRequestIds.length > 0) {
      return `Available ${offerRequestIds.length === 1 ? 'Offer' : 'Offers'}`
    }

    if (fulfillmentOrderIds && fulfillmentOrderIds.length > 0) {
      return `Available ${fulfillmentOrderIds.length === 1 ? 'Order' : 'Orders'}`
    }

    return 'Offers'
  }, [offerRequestIds, fulfillmentOrderIds])

  const pageSubtitle = useMemo(() => {
    if (offerRequestIds && offerRequestIds.length > 0) {
      return `You are viewing ${offerRequestIds.length === 1 ? 'an' : `${offerRequestIds.length} `} available ${offerRequestIds.length === 1 ? 'offer' : 'offers'}`
    }

    if (fulfillmentOrderIds && fulfillmentOrderIds.length > 0) {
      return `You are viewing ${fulfillmentOrderIds.length === 1 ? 'an' : `${fulfillmentOrderIds.length} `} available airport pickup ${fulfillmentOrderIds.length === 1 ? 'order' : 'orders'}`
    }

    return 'Browse available offers'
  }, [offerRequestIds, fulfillmentOrderIds])

  return (
    <Box sx={styles.container}>
      <UploadFlightItineraryPDFModal
        open={addFlightModalOpen}
        onClose={handleAddFlightModalClose}
        onSubmit={handleAddFlightModalSubmit}
        offerRequest={
          addFlightOfferContext && 'offerRequestId' in addFlightOfferContext
            ? { offerRequestId: addFlightOfferContext.offerRequestId.toString() }
            : undefined
        }
        fulfillmentOrder={
          addFlightOfferContext && 'orderId' in addFlightOfferContext
            ? { orderId: addFlightOfferContext.orderId }
            : undefined
        }
      />

      <PaginatedPage
        title={{
          text: pageTitle,
          id: pageTitle
        }}
        subtitle={pageSubtitle}
        headerButton={headerButton}
        isFetchingData={loading}
        fetchCall={fetchOffers}
        renderListData={OfferItemsView}
        emptyState={getEmptyState()}
        layout={
          (offerRequestIds && offerRequestIds.length === 1) || (fulfillmentOrderIds && fulfillmentOrderIds.length === 1)
            ? 'list'
            : 'masonry'
        }
        verticalFilters={
          (offerRequestIds && offerRequestIds.length > 0) || (fulfillmentOrderIds && fulfillmentOrderIds.length > 0)
            ? undefined
            : filterSections
        }
        searchBar={
          (offerRequestIds && offerRequestIds.length > 0) || (fulfillmentOrderIds && fulfillmentOrderIds.length > 0)
            ? undefined
            : searchBar
        }
        removeExtraPadding={
          (offerRequestIds && offerRequestIds.length > 0) || (fulfillmentOrderIds && fulfillmentOrderIds.length > 0)
            ? true
            : false
        }
        horizontalFilterTabs={horizontalFilterTabs}
        defaultActiveTabId={getUserRole() === UserRole.sender ? 'airport-pickup' : undefined}
        getItemKey={(item: unknown) => {
          const typedItem = item as OfferRequest | FulfillmentOrder
          if ('orderId' in typedItem) {
            return (typedItem as FulfillmentOrder).orderId
          }

          return (typedItem as OfferRequest).offerRequestId.toString()
        }}
      />
    </Box>
  )
}

export default Offers
